<script setup>
import { computed } from 'vue'
import { useClipboard } from '@vueuse/core'
import { getDashboardUrl, getStructureImageUrl } from '@/main'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { getChemicalTitle } from '@/utils/chemical'
import ChemLinks from './ChemLinks.vue'

const props = defineProps({
  chemical: {
    type: Object,
    required: true,
  },
  showImage: {
    type: Boolean,
    default: true,
  },
  secondaryTitle: {
    type: String,
  },
  propsToShow: {
    type: Array,
  },
  showInRow: {
    type: Boolean,
    default: false,
  },
  keyValueStacked: {
    type: Boolean,
    default: false,
  },
  truncateProps: {
    type: Boolean,
    default: false,
  },
  showLinks: {
    type: Boolean,
    default: false,
  },
})

const { text, copy, copied, isSupported } = useClipboard({ legacy: true })

const title = computed(() => getChemicalTitle(props.chemical) ?? 'N/A')

const fieldsToShow = computed(() =>
  props.keyValueStacked
    ? [
        { key: 'copy', label: '', tdClass: 'width-2rem' },
        { key: 'value', label: 'Value' },
      ]
    : [
        { key: 'copy', label: '', tdClass: 'width-2rem' },
        { key: 'key', label: 'Property', tdClass: 'font-weight-bold' },
        { key: 'value', label: 'Value' },
      ],
)

const defaultChemicalPropsEntries = [
  ['cid', 'DTXCID'],
  ['sid', 'DTXSID'],
  ['casrn', 'CAS'],
  ['smiles', 'SMILES'],
  ['inchi', 'InChI'],
  ['inchiKey', 'InChIKey'],
  ['averageMass', 'Average Mass'],
  ['monoisotopicMass', 'Monoisotopic Mass'],
]

const visiblePropsLabelMap = computed(
  () =>
    new Map([
      ...(props.propsToShow?.length
        ? defaultChemicalPropsEntries.filter(([key]) => props.propsToShow.includes(key))
        : defaultChemicalPropsEntries),
    ]),
)

const chemicalPropItems = computed(() => {
  const standardProps = Object.entries(props.chemical)
    .map(([key, value]) => {
      if (!visiblePropsLabelMap.value.has(key)) {
        return null
      }

      if (key === 'averageMass') {
        value = Number.isFinite(value) ? value.toFixed(3) : value
      } else if (key === 'monoisotopicMass') {
        value = Number.isFinite(value) ? value.toFixed(6) : value
      }

      return {
        key: visiblePropsLabelMap.value.get(key),
        value,
        class: 'text-truncate',
      }
    })
    .filter((prop) => prop !== null && prop.value !== null)

  const additionalProps = props.chemical.additionalProps
    ? Object.entries(props.chemical.additionalProps).map(([key, value]) => ({
        key,
        value,
      }))
    : []

  return props.propsToShow
    ? standardProps
    : [
        ...standardProps,
        ...(additionalProps.length ? [{ _showDetails: true, key: '', value: '' }] : []),
        ...additionalProps,
      ]
})

const chemicalPropsClipboardText = computed(() =>
  chemicalPropItems.value
    .filter((prop) => !prop._showDetails)
    .map(({ key, value }) => `${key}: ${value}`)
    .join('\n'),
)

const chemicalImageUrl = computed(() => {
  if (props.chemical?.image) {
    return `data:image/svg+xml;base64,${btoa(props.chemical.image)}`
  }

  return getStructureImageUrl(props.chemical)
})
</script>

<template>
  <v-card
    class="d-flex flex-column"
    flat
  >
    <v-card-subtitle class="pb-1">
      <v-tooltip
        v-bind="defaultVTooltipProps"
        top
      >
        <template #activator="{ on }">
          <div
            v-on="on"
            class="text-truncate font-weight-bold"
          >
            {{ title }}
          </div>
        </template>

        {{ title }}
      </v-tooltip>

      <div
        v-if="secondaryTitle"
        class="small text-muted"
        style="line-height: 1"
      >
        {{ secondaryTitle }}
      </div>
    </v-card-subtitle>

    <v-card-text>
      <div
        class="d-flex"
        :class="showInRow ? 'flex-row-reverse' : 'flex-column'"
      >
        <div
          class="bg-light p-1"
          :class="showInRow ? 'd-flex flex-1 align-items-center tw-min-w-0' : ''"
        >
          <v-img
            v-if="chemicalImageUrl && showImage"
            :src="chemicalImageUrl"
            alt="Chemical structure"
            contain
            height="7rem"
          />
        </div>

        <v-divider
          v-if="!showInRow"
          class="my-1"
        />

        <div
          class="d-flex flex-column"
          style="max-height: 25rem; overflow-y: auto"
          :class="showInRow ? 'flex-1 tw-min-w-0 ' : ''"
        >
          <b-table-lite
            :fields="fieldsToShow"
            :items="chemicalPropItems"
            borderless
            small
            thead-tr-class="d-none"
            :responsive="false"
            class="mb-0"
            style="table-layout: fixed; width: 100%"
          >
            <template
              v-if="isSupported"
              #cell(copy)="{ item }"
            >
              <v-btn
                v-if="!item._showDetails"
                icon
                x-small
                :title="`Copy ${item.key}`"
                :aria-label="`Copy ${item.key}`"
                @click="copy(item.value)"
              >
                <v-icon small>{{ copied && text === item.value ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              </v-btn>
            </template>

            <template #cell(value)="{ item }">
              <v-tooltip
                v-bind="defaultVTooltipProps"
                top
              >
                <template #activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                  >
                    <div
                      v-if="keyValueStacked"
                      class="text-muted small"
                    >
                      {{ item.key }}
                    </div>

                    <div :class="{ 'text-truncate': truncateProps }">
                      {{ item.value }}
                    </div>
                  </div>
                </template>

                {{ item?.value }}
              </v-tooltip>
            </template>

            <template #row-details>Additional Properties</template>
          </b-table-lite>

          <div
            v-if="chemicalPropItems.length > 1"
            class="pt-1 mt-auto"
          >
            <v-btn
              small
              depressed
              aria-label="Copy all properties"
              @click="copy(chemicalPropsClipboardText)"
            >
              <v-icon small>
                {{ copied && text === chemicalPropsClipboardText ? 'mdi-check' : 'mdi-content-copy' }}
              </v-icon>
              Copy all
            </v-btn>
          </div>
        </div>
      </div>

      <template v-if="chemical?.sid && showLinks">
        <v-divider class="mt-2 mb-0" />

        <ChemLinks :sid="chemical?.sid" />
      </template>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
::v-deep .width-2rem {
  width: 2rem;
}

::v-deep .v-card__subtitle a {
  cursor: pointer !important;
}
</style>
